/**
 * Planeador regional hierárquico (módulo puro, sem rede e sem custo).
 *
 * Recebe o pedido já classificado como país/região/multi-cidade e devolve o
 * ESQUELETO do plano: que bases usar, quantos dias em cada uma e que
 * deslocações interurbanas serão necessárias. Só depois deste esqueleto é que
 * o pipeline gasta chamadas a Places/Routes.
 */

import { pickRegionalBases, type RegionalBase } from "../catalog/regions";
import { haversineMeters } from "./geo";
import { formatTravelDuration } from "../route-sanity";

export type RegionalTransport = "car" | "transit" | "train" | "walk" | "bike";

export type RegionalPlanInput = {
  countryCode?: string;
  scopeName: string;
  days: number;
  themes?: RegionalBase["themes"];
  transport: RegionalTransport;
  preferAffordable?: boolean;
};

export type RegionalLeg = {
  fromCity: string;
  toCity: string;
  distanceKm: number;
  estimatedHours: number;
  label: string;
};

export type RegionalPlanSkeleton = {
  ok: true;
  bases: Array<{ base: RegionalBase; days: number }>;
  legs: RegionalLeg[];
  totalTravelHours: number;
  notes: string[];
};

export type RegionalPlanRefusal = {
  ok: false;
  reason: "no_curated_bases" | "too_much_travel";
  message: string;
};

export type RegionalPlanResult = RegionalPlanSkeleton | RegionalPlanRefusal;

/** Velocidades médias porta-a-porta para estimativa prévia (km/h). */
const AVERAGE_SPEED_KMH: Record<RegionalTransport, number> = {
  car: 70,
  transit: 60,
  train: 75,
  bike: 15,
  walk: 4.5,
};

/** Tempo máximo aceitável em deslocações interurbanas, por dia. */
const MAX_TRAVEL_HOURS_PER_DAY = 3.5;

function allocateDays(baseCount: number, days: number): number[] {
  const perBase = Math.floor(days / baseCount);
  const rest = days - perBase * baseCount;
  return Array.from({ length: baseCount }, (_, i) => perBase + (i < rest ? 1 : 0));
}

export function planRegionalSkeleton(input: RegionalPlanInput): RegionalPlanResult {
  const days = Math.max(1, Math.round(input.days || 1));
  const preferTransit = input.transport === "transit" || input.transport === "train";
  const chosen = pickRegionalBases({
    countryCode: input.countryCode,
    days,
    themes: input.themes,
    preferTransit,
    preferAffordable: input.preferAffordable,
  });

  if (!chosen.length) {
    return {
      ok: false,
      reason: "no_curated_bases",
      message: `Ainda não tenho bases verificadas para ${input.scopeName}. Diz-me por que cidades queres começar e eu construo o roteiro à volta delas.`,
    };
  }

  // Ordena geograficamente (vizinho mais próximo) para minimizar deslocações.
  const ordered: RegionalBase[] = [chosen[0]];
  const pool = chosen.slice(1);
  while (pool.length) {
    const last = ordered[ordered.length - 1];
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    pool.forEach((candidate, index) => {
      const distance = haversineMeters(last, candidate);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });
    ordered.push(pool.splice(bestIndex, 1)[0]);
  }

  const speed = AVERAGE_SPEED_KMH[input.transport] ?? AVERAGE_SPEED_KMH.car;
  const legs: RegionalLeg[] = [];
  for (let i = 1; i < ordered.length; i++) {
    const from = ordered[i - 1];
    const to = ordered[i];
    // 1.25× em linha reta aproxima a distância real por estrada/carril.
    const distanceKm = (haversineMeters(from, to) / 1000) * 1.25;
    const estimatedHours = distanceKm / speed;
    legs.push({
      fromCity: from.city,
      toCity: to.city,
      distanceKm: Math.round(distanceKm),
      estimatedHours: Math.round(estimatedHours * 100) / 100,
      label: `${from.city} → ${to.city} · ${Math.round(distanceKm)} km · ${formatTravelDuration(estimatedHours * 3600)}`,
    });
  }

  const totalTravelHours = legs.reduce((sum, leg) => sum + leg.estimatedHours, 0);
  if (totalTravelHours > MAX_TRAVEL_HOURS_PER_DAY * days) {
    // Tenta com menos bases antes de desistir (menos deslocações).
    if (ordered.length > 1) {
      const fewer = planRegionalSkeleton({ ...input, days: Math.max(1, days - 1) });
      if (fewer.ok) {
        return { ...fewer, bases: fewer.bases.map((b, i) => ({ ...b, days: allocateDays(fewer.bases.length, days)[i] })) };
      }
    }
    return {
      ok: false,
      reason: "too_much_travel",
      message: `Com ${days} dia(s) ficarias demasiado tempo em deslocações por ${input.scopeName}. Diz-me uma região para focar e faço um roteiro realista.`,
    };
  }

  const allocation = allocateDays(ordered.length, days);
  const notes: string[] = [];
  if (preferTransit) notes.push("Bases escolhidas com boa ligação ferroviária.");
  if (input.preferAffordable) notes.push("Prioridade a zonas de alojamento geralmente mais económicas.");

  return {
    ok: true,
    bases: ordered.map((base, index) => ({ base, days: allocation[index] })),
    legs,
    totalTravelHours: Math.round(totalTravelHours * 100) / 100,
    notes,
  };
}

/** Cidades coerentes derivadas do esqueleto — nunca um país tratado como cidade única. */
export function citiesFromRegionalSkeleton(result: RegionalPlanResult): string[] {
  return result.ok ? result.bases.map(({ base }) => base.city) : [];
}
